/**
 * This component will entice to login or join rewards by providing an upfront message.
 *
 * @module views/components/RewardsMessaging
 * @memberof -Common
 */
import './RewardsMessaging.scss';

import React, { useCallback, useEffect } from 'react';

import classNames from 'classnames';
import deepmerge from 'deepmerge';
import PropTypes from 'prop-types';

import useLoader from '@ulta/core/hooks/useLoader/useLoader';
import { useUserContext } from '@ulta/core/providers/UserContextProvider/UserContextProvider';
import { DXL_NAVIGATION_TYPE } from '@ulta/core/utils/constants/action';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import BlockMessage from '@ulta/components/BlockMessage/BlockMessage';
import PromotedCardDropdown from '@ulta/components/PromotedCardDropdown/PromotedCardDropdown';

import { superscriptText } from '@ulta/utils/superscriptText/superscriptText';

import PromotedCardMini from '../PromotedCardMini/PromotedCardMini';
import * as utils from './RewardsMessaging';
/**
 * Represents a RewardsMessaging component
 *
 * @method
 * @param {RewardsMessagingProps} props - React properties passed from composition
 * @returns RewardsMessaging
 */
export const RewardsMessaging = React.forwardRef( ( props, ref ) => {
  const { user } = useUserContext();
  const state = utils.getDerivedState( { props, user } );

  const {
    components,
    configureContainerQuery,
    displayAsMobile,
    dropdown,
    dropdownContent,
    hasRedirectAction,
    headline = '',
    includeDivider = defaultProps.includeDivider,
    invokeMutation,
    joinNowAction,
    loading,
    messages = defaultProps.messages,
    miniCardContent
  } = state;

  const [loader] = useLoader( { loading, delay: 600 } );
  const hasPromotedCardMiniContent = miniCardContent && !dropdown;
  const hasDropdownContent = dropdown && Object.values( dropdownContent ).some( value => value !== null );

  useEffect( () => {
    configureContainerQuery && configureContainerQuery( { maxWidth: 500 } );
  }, [configureContainerQuery] );

  /**
   * Rewards messaging can handle multiple action types:
   * 1. Join now mutation
   * 2. Legacy sign in
   * 3. Save for later overlay click
   * 4. Link/redirect
   * --------------------
   * 1. We assume that if a graphql key exists on the action
   * that it's a join now mutation.
   * --------------------
   * 2-4. If it's a legacy sign in or flyout request, we can pass that through
   * to PromotedCardMini where link/button will handle the rest.
   */
  const onCtaClick = useCallback( utils.composeJoinNow( { joinNowAction }, { invokeMutation } ), [joinNowAction] );

  if( !components.Main ){
    return null;
  }

  return (
    <div
      className={ classNames( 'RewardsMessaging', {
        'RewardsMessaging--border-bottom': includeDivider
      } ) }
      id={ props.id }
      ref={ ref }
    >
      { components.ErrorMessage && (
        messages.map(
          ( data, i ) =>
            (
              <div className='RewardsMessaging__blockErrorMessage'
                key={ `${i}-$messages-{props.componentKey}` }
              >
                <BlockMessage
                  message={ data.message }
                  backgroundColor={ data.backgroundColor }
                  borderColor={ data.borderColor }
                  textColor={ data.textColor }
                  dismissAction={ data.dismissAction }
                  action1={ data.action1 }
                  action2={ data.action2 }
                  action1Style='link'
                  actionIcon={ data.icon }
                />
              </div>
            )
        )

      ) }
      { loader }
      { hasDropdownContent && (
        <PromotedCardDropdown
          dataCapture={ dropdownContent.dataCapture }
          action={ dropdownContent.action }
          elements={ dropdownContent.elements }
          headline={ dropdownContent.headline }
          icon={ dropdownContent.icon }
          subheadline={ dropdownContent.subheadline }
          message={ dropdownContent.message }
          toolTip={ dropdownContent.toolTip }
        />
      ) }
      { hasPromotedCardMiniContent && (
        <PromotedCardMini
          column='true'
          invokeMutation={ invokeMutation }
          displayAsMobile={ displayAsMobile }
          onCtaClick={ !hasRedirectAction ? onCtaClick : undefined }
          { ...spreadProps( {
            ...miniCardContent,
            headline
          } ) }
        />
      ) }
    </div>
  );
} );

/**
 * @typedef {object} RewardsMessagingState
 * @property {object} components Component's render state
 * @property {object} joinNowAction DXL action for join now mutation
 * @property {object} miniCardContent Transfored PromotedCardMini model
 */

/**
 * 1. Provide fallbacks/default values in case of malformed data
 * 2. Determine derived state
 * 3. Determine main and child component rendering
 * 4. Return props + derived state
 * @method
 * @param {object} state
 * @param {RewardsMessagingProps} state.props
 * @param {{loginStatus:string}} state.user
 * @returns {RewardsMessagingState} RewardsMessagingState
 */
export const getDerivedState = ( data ) => {
  const { props = {}, user = {} } = data || {};

  const state = deepmerge( props, { joinNowAction: {} } );

  /*
   * 1.Destructure props you need to derive state from
   * -------------------------------------------------
   * 1a. Provide fallbacks/default values in case of malformed data
   */
  const { content = {}, dropdown = false } = state;
  const { action } = content || {};
  const { graphql, customHeaders, navigationType } = action || {};
  const { loginStatus } = user;

  /*
   * 2. Create all of the state your component needs here
   * ----------------------------------------------------
   */

  // Grab graphql for join mutation
  // TODO: This should come from DXL and the client should not be determining if it's a join
  //       action based on login status.
  if( graphql && loginStatus ){
    state.joinNowAction = {
      graphql: graphql,
      customHeaders: customHeaders,
      variables: {
        url: { path: !isServer() && global.location.pathname }
      }
    };
  }

  // Forces right align + cross button for flyout
  // TODO: This should come from DXL
  state.miniCardContent = { ...content };
  if( navigationType === DXL_NAVIGATION_TYPE.Flyout ){
    state.miniCardContent.action.crossButtonVisibility = true;
    state.miniCardContent.action.alignment = 'left';
  }
  if( navigationType === DXL_NAVIGATION_TYPE.Redirect ){
    state.hasRedirectAction = true;
  }

  /*
   * 3. Define which components to hide/show
   * --------------------------------------
   * 3a.`Main` is required
   * 3b. Add conditionally children components, e.g.:
   * --------------------------------------
   * {
   *    Main: should hide/show based on required props
   *    JoinButton: !!action.graphql
   *    LogoutButton: user.loginStatus === true
   * }
   */
  state.components = {
    Main: Object.keys( state.miniCardContent ).length !== 0,
    ErrorMessage: !!state.messages
  };

  state.headline = dropdown ? content?.headline :
    superscriptText( { title: content?.headline, character:'®', shouldAppend: false } );

  if( dropdown ){
    state.dropdownContent = { ...content };
  }

  /*
   * 4.Return derived state
   * ----------------------
   */
  return state;
};

/**
 * handleJoinNow function for button
 * @method
 * @param {Object} data - Passing pathname data as an argument
 * @param {Object} methods - Passing invokeMutation method as an argument
 */
export const composeJoinNow = ( data, methods ) => () => {
  const { joinNowAction } = data || {};
  const { invokeMutation } = methods || {};

  if( !joinNowAction?.graphql || !invokeMutation ){
    return;
  }

  invokeMutation( joinNowAction );
};

/**
 * Property type definitions
 * @typedef RewardsMessagingProps
 * @type {object}
 * @property {function} configureContainerQuery - Configures the container query
 * @property {object} content - Sets the content
 * @property {boolean} displayAsMobile - displayAsMobile
 * @property {boolean} dropdown - dropdown boolean to display PromotedCardDropdown
 * @property {string} id - Set the id of component
 * @property {boolean} includeDivider - Add the bottom border if divider required
 * @property {func} invokeMutation - Called on click event of Join Now action.
 * @property {array} messages - display an block error messages when authenticated guest not attached to rewards.
 */

/**
 * @type {RewardsMessagingProps}
 */
export const propTypes = {
  configureContainerQuery: PropTypes.func,
  content: PropTypes.shape( {
    action: PropTypes.object,
    headline: PropTypes.string
  } ),
  displayAsMobile: PropTypes.bool,
  dropdown: PropTypes.bool,
  id: PropTypes.string,
  includeDivider: PropTypes.bool,
  invokeMutation: PropTypes.func,
  messages: PropTypes.array
};
/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {bool} includeDivider = false, No border bottom by default
 * @property {array} messages = null, No blcok error messages by default
 */
export const defaultProps = {
  includeDivider: false,
  messages: null
};

RewardsMessaging.displayName = 'RewardsMessaging';
RewardsMessaging.propTypes = propTypes;
RewardsMessaging.defaultProps = defaultProps;

export default RewardsMessaging;
